import * as React from "react";
import { useGetCompanyRadarMetricsQuery } from "reducers/companyProfileApi";
import CompanyAchievementCard from "./CompanyAchievementCard";
import CompanyLevelCard from "./CompanyLevelCard";
import CompanyQuestCard from "./CompanyQuestCard";
import CompanyRadarCard from "./CompanyRadarCard";
import { RadarAxis } from "./CompanyRadarChart";
import LevelUpCelebrationModal from "./LevelUpCelebrationModal";
import { useLevelUpCelebration } from "./useLevelUpCelebration";

type CompanyRole = "seller" | "buyer";

interface CompanyProfileDashboardProps {
  companyCode?: string;
  role: CompanyRole;
}

const EMPTY_AXES: Record<CompanyRole, RadarAxis[]> = {
  seller: [
    { key: "speed", label: "成約速度", value: 0 },
    { key: "reliability", label: "信頼性", value: 0 },
    { key: "stability", label: "継続力", value: 0 },
    { key: "value", label: "価値", value: 0 },
    { key: "circulation", label: "環境貢献", value: 0 },
  ],
  buyer: [
    { key: "speed", label: "成約速度", value: 0 },
    { key: "reliability", label: "信頼性", value: 0 },
    { key: "stability", label: "継続力", value: 0 },
    { key: "circulation", label: "環境貢献", value: 0 },
  ],
};

const CompanyProfileDashboard: React.FC<CompanyProfileDashboardProps> = ({
  companyCode,
  role,
}) => {
  const { data, isLoading, isError } = useGetCompanyRadarMetricsQuery(
    { companyCode: companyCode ?? "" },
    { skip: !companyCode },
  );

  const { celebration, dismiss } = useLevelUpCelebration(companyCode);

  const metrics = data?.data.metrics;
  const axes: RadarAxis[] = metrics
    ? [
        { key: "speed", label: "成約速度", value: metrics.speed },
        {
          key: "reliability",
          label: "信頼性",
          value: metrics.reliability,
        },
        { key: "stability", label: "継続力", value: metrics.stability },
        ...(role === "seller"
          ? [
              {
                key: "value" as const,
                label: "価値",
                value: metrics.value as number,
              },
            ]
          : []),
        {
          key: "circulation",
          label: "環境貢献",
          value: metrics.circulation,
        },
      ]
    : EMPTY_AXES[role];

  return (
    <div className="flex w-full flex-col gap-[15px]">
      <div className="grid w-full grid-cols-1 gap-[15px] xl:grid-cols-[minmax(0,1fr)_350px]">
        <CompanyLevelCard companyCode={companyCode} />
        <CompanyRadarCard
          axes={axes}
          isLoading={isLoading || !companyCode}
          isError={isError}
        />
      </div>

      <div className="grid w-full grid-cols-1 gap-[15px] xl:grid-cols-[minmax(0,1fr)_350px]">
        <CompanyQuestCard />
        <CompanyAchievementCard companyCode={companyCode} />
      </div>

      {celebration && (
        <LevelUpCelebrationModal
          open
          fromLevel={celebration.fromLevel}
          toLevel={celebration.toLevel}
          onClose={dismiss}
        />
      )}
    </div>
  );
};

export default CompanyProfileDashboard;
